import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { PageHeader, TableSkeleton } from "@/components/common/Common";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, X, Search, Network } from "lucide-react";

const MATRIX_MODULES = [
  { key: "users", label: "Users" },
  { key: "branches", label: "Branches" },
  { key: "roles", label: "Roles" },
  { key: "reports", label: "Reports" },
  { key: "audit_logs", label: "Audit Logs" },
  { key: "settings", label: "Settings" },
];
const ACTIONS = ["view", "create", "edit", "delete"];

export default function AccessControlPage() {
  const { t } = useApp();
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => { api.get("/access-control").then((r) => { setRows(r.data); setSelected(r.data[0] || null); }).catch(() => {}); }, []);

  const has = (perm) => selected?.permissions?.includes(perm);
  const filtered = (rows || []).filter((r) => !search || `${r.name} ${r.email}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <PageHeader title={t("accessControl")} subtitle="User → Roles → Branches → Permissions" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* user list */}
        <div className="bg-card border border-border rounded-md">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="pl-9" data-testid="ac-search" />
            </div>
          </div>
          {rows === null ? <TableSkeleton cols={1} /> : (
            <div className="max-h-[560px] overflow-y-auto divide-y divide-border">
              {filtered.map((r) => (
                <button key={r.id} onClick={() => setSelected(r)} data-testid={`ac-user-${r.id}`}
                  className={`w-full text-left px-4 py-3 transition-colors ${selected?.id === r.id ? "bg-primary/5 border-l-2 border-primary" : "hover:bg-muted/50"}`}>
                  <p className="font-medium text-sm">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.roles.join(", ")}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* detail */}
        <div className="lg:col-span-2 space-y-4">
          {selected ? (
            <>
              <div className="bg-card border border-border rounded-md p-5">
                <h3 className="font-heading font-bold text-lg">{selected.name}</h3>
                <p className="text-sm text-muted-foreground">{selected.email}</p>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div><p className="text-xs text-muted-foreground uppercase mb-1">{t("roles")}</p><div className="flex flex-wrap gap-1">{selected.roles.map((r) => <Badge key={r} variant="secondary">{r}</Badge>)}</div></div>
                  <div><p className="text-xs text-muted-foreground uppercase mb-1">{t("branches")}</p><div className="flex flex-wrap gap-1">{selected.branches.map((b) => <Badge key={b} variant="outline">{b}</Badge>)}</div></div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-md p-5">
                <h4 className="font-heading font-semibold text-sm mb-4">Permission Matrix</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-border">
                      <th className="text-left py-2 font-medium">Module</th>
                      {ACTIONS.map((a) => <th key={a} className="py-2 font-medium capitalize text-center w-20">{a}</th>)}
                    </tr></thead>
                    <tbody>
                      {MATRIX_MODULES.map((m) => (
                        <tr key={m.key} className="border-b border-border last:border-0" data-testid={`ac-matrix-${m.key}`}>
                          <td className="py-2.5 font-medium">{m.label}</td>
                          {ACTIONS.map((a) => (
                            <td key={a} className="text-center">
                              {has(`${m.key}.${a}`) ? <Check className="h-4 w-4 text-[hsl(var(--success))] mx-auto" /> : <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground mt-4">Permissions are derived from the user's assigned roles. Edit a role under <span className="font-medium">Roles</span> to change access.</p>
              </div>
            </>
          ) : <div className="bg-card border border-border rounded-md p-10 text-center text-muted-foreground text-sm"><Network className="h-6 w-6 mx-auto mb-2" />Select a user to view access</div>}
        </div>
      </div>
    </div>
  );
}
